"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FormContext = exports.useFormContext = exports.useIsForm = void 0;
const react_1 = require("react");
const lib_1 = require("./lib");
const store_1 = require("./store");
function useIsForm() {
    const context = (0, react_1.useContext)(lib_1.InternalFormContext);
    return Boolean(context);
}
exports.useIsForm = useIsForm;
function useFormContext() {
    const formContext = (0, react_1.useContext)(lib_1.InternalFormContext);
    const dispatch = (0, react_1.useContext)(lib_1.InternalFormDispatchContext);
    if (!formContext || !dispatch) {
        throw new Error('Critical: Components using useFormContext() must be contained by <Form> or <Input> component.');
    }
    const { inputs } = formContext;
    const setValue = (0, store_1.useFormSetter)();
    const getValue = (0, store_1.useFormGetter)();
    const getSerializedValue = (0, react_1.useCallback)((name) => {
        const controller = inputs.getState()[name];
        if (controller) {
            const { serialize, value } = controller;
            return serialize ? serialize(value) : value;
        }
    }, [inputs]);
    const { pages, successPage, activePage, submit, submitting = false, maxPageReached = 0, aggregators = {}, errors, } = formContext;
    const activePageIndex = typeof activePage === 'string'
        ? activePage === successPage
            ? pages.length
            : pages.indexOf(activePage)
        : -1;
    const activePageNumber = activePageIndex < 0 ? 0 : (activePageIndex || 0) + 1;
    const getFirstErrorPage = (0, react_1.useCallback)(() => {
        for (const page of pages) {
            const pageInputs = Object.entries(formContext.inputs.getState()).filter(([, input]) => input.page === page);
            const error = pageInputs.find(([, input]) => input.error);
            if (error) {
                return error[1].page;
            }
        }
    }, [formContext.inputs, pages]);
    const setActivePage = (0, react_1.useCallback)((page) => {
        if (page <= (maxPageReached !== null && maxPageReached !== void 0 ? maxPageReached : 0)) {
            const firstErrorPage = getFirstErrorPage();
            if (firstErrorPage && pages.indexOf(firstErrorPage) < page) {
                dispatch('SET_ACTIVE_PAGE', pages.indexOf(firstErrorPage) + 1);
            }
            else {
                dispatch('SET_ACTIVE_PAGE', page);
            }
        }
    }, [dispatch, getFirstErrorPage, maxPageReached, pages]);
    const getInputs = (0, react_1.useCallback)(() => {
        const { data, toAggregate } = (0, lib_1.buildFormData)(inputs.getState());
        return Object.assign(Object.assign({}, data), (0, lib_1.resolveAggregators)(aggregators, toAggregate));
    }, [aggregators, inputs]);
    const removeInput = (0, react_1.useCallback)((name) => {
        dispatch('REMOVE_INPUT', name);
    }, [dispatch]);
    const removeAggregator = (0, react_1.useCallback)((name) => {
        dispatch('REMOVE_AGGREGATOR', name);
    }, [dispatch]);
    const registerAggregator = (0, react_1.useCallback)((data) => dispatch('REGISTER_AGGREGATOR', data), [dispatch]);
    const value = (0, react_1.useMemo)(() => ({
        activePage: activePageNumber,
        maxPageReached,
        totalPages: pages.length + (successPage ? 1 : 0),
        setActivePage,
        successPage: activePage === successPage,
        submit,
        getValue,
        getSerializedValue,
        setValue,
        submitting,
        getInputs,
        removeInput,
        removeAggregator,
        registerAggregator,
        errors,
    }), [
        activePageNumber,
        maxPageReached,
        pages.length,
        successPage,
        setActivePage,
        activePage,
        submit,
        getValue,
        getSerializedValue,
        setValue,
        submitting,
        getInputs,
        removeInput,
        removeAggregator,
        registerAggregator,
        errors,
    ]);
    if (activePageNumber > 1 && activePage !== successPage) {
        value.prevPage = () => dispatch('SET_ACTIVE_PAGE', activePageNumber - 1);
    }
    return value;
}
exports.useFormContext = useFormContext;
function FormContext({ children, }) {
    const formContext = useFormContext();
    return children(formContext);
}
exports.FormContext = FormContext;
